import { useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Heading2, Heading3, ImagePlus, Italic, Link2, List, ListOrdered, Quote, Redo2, Underline, Undo2, Unlink } from "lucide-react";
import { mediaUrl, uploadMedia } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const absolutizeHtml = html => (html || "").replace(/src="\/api\/files\//g, `src="${BACKEND}/api/files/`);

export const RichHtml = ({ html, className = "", testId }) => (
  <div className={`rich-content ${className}`} data-testid={testId} dangerouslySetInnerHTML={{ __html: absolutizeHtml(html) }} />
);

const Btn = ({ on, active, title, children, testId, disabled }) => (
  <button type="button" onMouseDown={e => e.preventDefault()} onClick={on} className={active ? "active" : ""} title={title} disabled={disabled} data-testid={testId}>{children}</button>
);

export const RichTextEditor = ({ value, onChange, placeholder = "Write the full article here…", testId = "rich-editor" }) => {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, autolink: true, defaultProtocol: "https" }, heading: { levels: [2, 3] } }), Image.configure({ inline: false, allowBase64: false }), Placeholder.configure({ placeholder })],
    content: absolutizeHtml(value || ""),
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML().split(`${BACKEND}/api/files/`).join("/api/files/")),
    editorProps: { attributes: { class: "rich-editor-body", "data-testid": `${testId}-body` } },
  });

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (https://…)", prev);
    if (url === null) return;
    if (!url) return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.startsWith("http") ? url : `https://${url}`, target: "_blank" }).run();
  };
  const addImageUrl = () => { const url = window.prompt("Image URL"); if (url) editor.chain().focus().setImage({ src: url }).run(); };
  const pick = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try { const d = await uploadMedia(f); editor.chain().focus().setImage({ src: mediaUrl(d.url), alt: f.name }).run(); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); e.target.value = ""; }
  };
  if (!editor) return <div className="rich-editor" data-testid={testId}><div className="hint">Loading editor…</div></div>;

  return (
    <div className="rich-editor" data-testid={testId}>
      <div className="rich-toolbar">
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testId={`${testId}-h2`}><Heading2 size={15} /></Btn>
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testId={`${testId}-h3`}><Heading3 size={15} /></Btn>
        <i />
        <Btn on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId={`${testId}-bold`}><Bold size={15} /></Btn>
        <Btn on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId={`${testId}-italic`}><Italic size={15} /></Btn>
        <Btn on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testId={`${testId}-underline`}><Underline size={15} /></Btn>
        <i />
        <Btn on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId={`${testId}-bullets`}><List size={15} /></Btn>
        <Btn on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId={`${testId}-numbers`}><ListOrdered size={15} /></Btn>
        <Btn on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId={`${testId}-quote`}><Quote size={15} /></Btn>
        <i />
        <Btn on={setLink} active={editor.isActive("link")} title="Add link" testId={`${testId}-link`}><Link2 size={15} /></Btn>
        {editor.isActive("link") && <Btn on={() => editor.chain().focus().unsetLink().run()} title="Remove link" testId={`${testId}-unlink`}><Unlink size={15} /></Btn>}
        <Btn on={() => fileRef.current?.click()} title="Upload image" disabled={busy} testId={`${testId}-image-upload`}><ImagePlus size={15} /> <span>{busy ? "Uploading…" : "Image"}</span></Btn>
        <Btn on={addImageUrl} title="Image from URL" testId={`${testId}-image-url`}><span>URL</span></Btn>
        <i />
        <Btn on={() => editor.chain().focus().undo().run()} title="Undo" testId={`${testId}-undo`}><Undo2 size={15} /></Btn>
        <Btn on={() => editor.chain().focus().redo().run()} title="Redo" testId={`${testId}-redo`}><Redo2 size={15} /></Btn>
        <input ref={fileRef} type="file" hidden accept="image/*" onChange={pick} data-testid={`${testId}-file`} />
      </div>
      <EditorContent editor={editor} />
      {err && <small className="field-error" data-testid={`${testId}-error`}>{err}</small>}
    </div>
  );
};
